//Components
import DialogDelete from '../../components/DialogDelete/DialogDelete';
import SurveyTemplateDialog from '../../components/SurveyTemplateDialog/SurveyTemplateDialog';
import Module from '../../../../../components/layout/Module';
import DataGrid from '../../../../table/DataGrid';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import Box from '@mui/material/Box';
import TextAuditLog from '../../../../text/TextAuditLog';
import IconTooltipButton from '../../../../button/IconTooltipButton';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ExportTemplate from '../../components/ExportTemplate/ExportTemplate';
import ResponsiveChips from '../../components/ResponsiveChips/ResponsiveChips';

//Other dependencies
import useFetchData from '../../../../../hooks/useFetchData';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  loadSurveyTemplateData,
  checkedTemplateIds
} from '../../../../../store/actions/dataEntrySurveyTemplatesActions';
import {
  getTemplate,
  deleteTemplate,
  exportTemplates,
  cloneTemplate
} from '../../../../../services/data-entry/templates.service';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { updateTemplate } from '../../../../../store/actions/dataEntryTemplateGeneratorActions';
import { alertReset } from '../../../../../store/actions/alert.actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { selectAllOption } from '../../constants/autocomplete';
import { normalizeFileName } from '../../../../../utils/file';

const SurveyTemplateList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [filter, setFilter] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPrevieDialog, setShowPrevieDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const permissions = useSelector((state) => state?.session?.permissions?.templatesurveygenerator ?? {});
  const list = useSelector((state) => state?.dataEntrySurveyTemplates?.templates?.list ?? []);
  const loading = useSelector((state) => state?.dataEntrySurveyTemplates?.templates?.loading ?? []);
  const totalRecords = useSelector((state) => state?.dataEntrySurveyTemplates?.templates?.totalRecords ?? []);
  const checkedIds = useSelector((state) => state?.dataEntrySurveyTemplates?.templates?.checkedIds ?? []);
  const skip = useSelector((state) => state?.datatable?.surveyTemplates?.skip || 0);
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyTemplates?.rowsPerPage || 100);
  const columnsTable = useSelector((state) => state?.datatable?.surveyTemplates?.columnsTable || []);
  const { data: dataRoles = [] } = useFetchData('roles/standard');
  const roles = dataRoles ?? [];

  const onLoadData = useCallback(() => {
    dispatch(loadSurveyTemplateData(skip * rowsPerPage, rowsPerPage, filter, 'survey'));
    dispatch(alertReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, rowsPerPage, filter]);

  const cloneExistingTemplate = async (templateId) => {
    try {
      const { data, error } = await cloneTemplate(templateId);
      if (error || !data) {
        enqueueSnackbar('We could not clone this template', {
          variant: 'error'
        });
      } else {
        enqueueSnackbar('Template cloned successfully', {
          variant: 'success'
        });
        setSelectedRow(null);
        onLoadData();
      }
    } catch (error) {
      enqueueSnackbar('We could not clone this template', {
        variant: 'error'
      });
    }
  };

  const setCurrentRow = async (row, action) => {
    const record = JSON.parse(JSON.stringify(list.find((x) => x._id === row._id)));
    if (record) {
      if ('delete' === action) {
        setSelectedRow(record);
        setShowDialog(true);
      }
      if ('edit' === action) {
        try {
          setIsDeleting(true);

          const { data, error } = await getTemplate(record?._id);

          if (!data || error) {
            enqueueSnackbar('We could not get the template', {
              variant: 'error'
            });
          } else {
            if (data) {
              const payload = {
                _id: data?._id || '',
                title: data?.title || '',
                yearLevels: data?.yearLevels.map((year) => year._id) || [],
                roles: data?.roles.map((year) => year._id) || [],
                error: {},
                questions: data?.questions || [],
                isEditable: data?.isEditable ?? false
              };

              dispatch(updateTemplate(payload));
              navigate(`/dashboard/data-entry/generator/survey-templates/edit/${row._id}`);
            } else {
              enqueueSnackbar('We could not get the template', {
                variant: 'error'
              });
            }
          }
        } catch (error) {
          enqueueSnackbar('We could not get the template', {
            variant: 'error'
          });
        }
      }
      if ('clone' === action) {
        cloneExistingTemplate(record?._id);
      }
    }
  };

  const getNumberOfQuestions = (questions = []) => {
    const numberOfQuestion = questions.filter((question) => question?.type?.value !== 'groupreference');

    return numberOfQuestion.length || 0;
  };

  const onCancelDialog = () => {
    setShowDialog(false);
    setSelectedRow(null);
  };

  const onConfirmDialog = async () => {
    try {
      setIsDeleting(true);

      const { error } = await deleteTemplate(selectedRow?._id);

      if (error) {
        enqueueSnackbar('We could not delete the template', {
          variant: 'error'
        });
      } else {
        onLoadData();
      }
    } catch (error) {
      enqueueSnackbar('We could not delete the template', { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDialog(false);
    }
  };

  const getAvailableRoles = () => {
    return roles?.map((role) => ({
      id: role._id,
      label: role.name,
      value: role.id
    }));
  };

  const avaliableRoles = getAvailableRoles();
  const rolesToRender = avaliableRoles?.length > 0 ? [selectAllOption, ...avaliableRoles] : [];
  const selectedRoles = rolesToRender?.filter((role) => filter?.roles?.includes(role.id));

  const onUpdateFilters = (newFilter = {}) => {
    const { roles = [] } = newFilter;

    if (Array.isArray(roles) && roles.length > 0) {
      const alreadyAllSelected = filter?.roles?.includes('all');
      const includesAll = roles?.filter((item) => item === 'all').length > 0;

      //Remove !== all
      if (alreadyAllSelected && includesAll) {
        const filteredList = roles?.filter((item) => item !== 'all');
        const newFilterCopy = { ...newFilter, roles: filteredList || [] };
        setFilter({ ...filter, ...newFilterCopy });
        return;
      }

      //Select all
      if (!alreadyAllSelected && includesAll) {
        const allRoles = avaliableRoles?.map((year) => year.id);
        const newFilterCopy = { ...newFilter, roles: [...allRoles, 'all'] };
        setFilter({ ...filter, ...newFilterCopy });
        return;
      }

      //Add list specific
      if (!alreadyAllSelected && !includesAll) {
        if (roles.length === avaliableRoles.length) {
          const newFilterCopy = { ...newFilter, roles: [...roles, 'all'] };
          setFilter({ ...filter, ...newFilterCopy });
        } else {
          setFilter({ ...filter, ...newFilter });
        }

        return;
      }

      //Remove all
      if (alreadyAllSelected && !includesAll) {
        const newFilterCopy = { ...newFilter, roles: [] };
        setFilter({ ...filter, ...newFilterCopy });
        return;
      }
    } else {
      setFilter({ ...filter, ...newFilter });
    }
  };

  const onChecked = (ids) => dispatch(checkedTemplateIds(ids));

  const onCheckedAll = (checked) => {
    let idsToCheck = [];

    if (checked && checkedIds?.length !== totalRecords) {
      idsToCheck = list?.map((item) => String(item._id));
    }

    onChecked(idsToCheck);
  };

  const onCheckOne = (id, checked) => {
    if (checked) {
      onChecked([...checkedIds, id]);
    } else {
      onChecked(checkedIds?.filter((item) => item !== id));
    }
  };

  const onExportTemplates = async (templateIds = []) => {
    try {
      if (templateIds?.length > 0) {
        const record = JSON.parse(JSON.stringify(list.find((x) => x._id === templateIds[0])));
        const fileName =
          templateIds?.length > 1
            ? `import_survey-templates-list-${templateIds?.length}`
            : 'import_survey-template-' + normalizeFileName(record?.title ?? '' + new Date().toISOString());

        const blob = await exportTemplates(templateIds, 'survey');

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = `${fileName}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        onCheckedAll(false);
        enqueueSnackbar('Templates exported successfully. Find the file on your download folder', {
          variant: 'success'
        });
      }
    } catch (error) {
      enqueueSnackbar('We could not export templates, please try again', {
        variant: 'error'
      });
    }
  };

  const onPreviewTemplate = async (templateId) => {
    const record = JSON.parse(JSON.stringify(list.find((x) => x._id === templateId)));

    if (record) {
      setSelectedRow(record);
      setShowPrevieDialog(true);
    }
  };

  const onImportTemplates = () => {
    setOpenImportModal(true);
    onCheckedAll(false);
  };

  useEffect(() => {
    onLoadData();
  }, [onLoadData]);

  useEffect(() => {
    onCheckedAll(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Module.BodyHeader py='4' className='border-b border-b-2 border-primary'>
        <Box
          style={{
            width: '100%',
            display: 'flex',
            alignContent: 'center',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box display={'flex'}>
            <Box style={{ minWidth: '300px' }}>
              <MultiSelectDropdown
                optionList={rolesToRender ?? []}
                limit={2}
                value={selectedRoles ?? []}
                onUpdateFilter={onUpdateFilters}
                filterKey='roles'
                label='Roles'
                placeholder='Choose roles'
                open={open}
                setOpen={setOpen}
              />
            </Box>
          </Box>
          <Box display={'flex'} gap={2}>
            {permissions?.import && (
              <Button
                variant='outlined'
                startIcon={<FontAwesomeIcon icon='fa-cloud-arrow-up' />}
                color='primary'
                aria-label='export templates'
                onClick={onImportTemplates}
                disabled={loading || checkedIds?.length > 0}
              >
                Import
              </Button>
            )}
            {permissions?.export && (
              <Button
                variant='contained'
                startIcon={<FontAwesomeIcon icon='fa-cloud-arrow-down' />}
                color='primary'
                aria-label='export templates'
                onClick={() => onExportTemplates(checkedIds)}
                disabled={checkedIds?.length === 0 || loading}
              >
                Export
              </Button>
            )}
          </Box>
        </Box>
      </Module.BodyHeader>
      <Module.BodyContainer className='overflow-y-auto'>
        <DataGrid
          className='pb-6'
          id='table-survey-templates'
          rows={list.map((row, index) => ({
            ...row,
            index: index + 1 + rowsPerPage * skip,
            name: row.title || '-',
            role: (
              <ResponsiveChips
                minDisplay={3}
                maxDisplay={15}
                chipVariant='outlined'
                items={row?.roles ?? []}
                field='name'
              />
            ),
            questions: getNumberOfQuestions(row?.questions),
            log: (
              <TextAuditLog
                updatedAt={row.updatedAt}
                updatedBy={row.updatedBy}
                createdBy={row.createdBy}
                createdAt={row.createdAt}
              />
            ),
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions?.list && (
                  <IconTooltipButton
                    id={`preview-${row._id}`}
                    disabled={loading || checkedIds?.length > 0}
                    title='Preview'
                    icon='expand-alt'
                    onClick={() => onPreviewTemplate(row._id)}
                  />
                )}
                {permissions?.edit && (
                  <IconTooltipButton
                    id={`edit-${row._id}`}
                    disabled={loading || checkedIds?.length > 0}
                    title='Edit'
                    icon='pencil'
                    onClick={() => setCurrentRow(row, 'edit')}
                  />
                )}
                {permissions?.create && (
                  <IconTooltipButton
                    id={`clone-${row._id}`}
                    disabled={loading}
                    title='Make a copy'
                    icon='clone'
                    onClick={() => setCurrentRow(row, 'clone')}
                  />
                )}
                {permissions?.export && (
                  <IconTooltipButton
                    id={`export-${row._id}`}
                    disabled={loading || checkedIds?.length > 0}
                    title='Export'
                    icon='fa-cloud-arrow-down'
                    onClick={() => onExportTemplates([row._id])}
                  />
                )}

                {permissions?.delete && (
                  <IconTooltipButton
                    id={`delete-${row._id}`}
                    disabled={loading || checkedIds?.length > 0}
                    title='Delete'
                    icon='fa-trash'
                    onClick={() => setCurrentRow(row, 'delete')}
                  />
                )}
              </div>
            ),
            checkIds: (
              <>
                {permissions?.export && (
                  <Checkbox
                    id={`checkbox-id-user-${row._id}`}
                    checked={checkedIds.includes(row._id)}
                    onChange={(e) => onCheckOne(row._id, e.target.checked)}
                    color='primary'
                    disabled={loading}
                  />
                )}
              </>
            )
          }))}
          loading={loading}
          labelNoRows='No survey templates found'
          nameModule='surveyTemplates'
          headers={[...columnsTable]}
          totalRows={totalRecords}
          skip={skip}
          rowsPerPage={rowsPerPage}
          columns={[
            {
              label: '#',
              key: 'index',
              align: 'center',
              width: 10,
              show: true,
              hide: false
            },
            {
              show: permissions['export'],
              key: 'checkIds',
              align: 'center',
              width: 15,
              label: (
                <Checkbox
                  id={`checkbox-id-user-header`}
                  checked={checkedIds.length > 0 && checkedIds.length === totalRecords}
                  indeterminate={checkedIds.length > 0 && checkedIds.length < totalRecords}
                  onChange={(e) => onCheckedAll(e.target.checked)}
                  color='primary'
                  disabled={loading}
                />
              )
            },
            ...columnsTable,
            {
              label: 'Actions',
              key: 'actions',
              align: 'center',
              show: true,
              hide: false
            }
          ]}
          actionsSetting={
            checkedIds.length > 0 && (
              <Typography className='min-w-100px' align='right'>
                Rows selected {checkedIds.length}
              </Typography>
            )
          }
        />
      </Module.BodyContainer>

      <DialogDelete
        show={showDialog}
        onCancel={onCancelDialog}
        onConfirm={onConfirmDialog}
        loading={isDeleting}
        type='delete'
        item={selectedRow?.title}
        module='template'
      />
      <ExportTemplate
        openModal={openImportModal}
        setOpenModal={setOpenImportModal}
        templateType='survey'
        title={'Import Survey Templates'}
      />
      <SurveyTemplateDialog
        id={`template-survey-list-modal`}
        open={showPrevieDialog}
        onCancel={() => setShowPrevieDialog(false)}
        onConfirm={() => setShowPrevieDialog(false)}
        loading={false}
        title={selectedRow?.title || 'Template Preview'}
        onCloseHeader={true}
        maxWidth='lg'
        showActions={false}
        questions={selectedRow?.questions?.map((item) => {
          return {
            ...item,
            answer: [],
            error: null
          };
        })}
      />
    </>
  );
};

export default SurveyTemplateList;
